import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react'
import { clsx } from 'clsx'

export interface TableProps {
  children: ReactNode
  className?: string
}

export function Table({ children, className }: TableProps) {
  return (
    <div className={clsx('overflow-x-auto', className)}>
      <table className="min-w-full divide-y divide-neutral-200">{children}</table>
    </div>
  )
}

export interface TableHeadProps {
  children: ReactNode
  className?: string
}

export function TableHead({ children, className }: TableHeadProps) {
  return <thead className={clsx('bg-neutral-50', className)}>{children}</thead>
}

export interface TableBodyProps {
  children: ReactNode
  className?: string
}

export function TableBody({ children, className }: TableBodyProps) {
  return (
    <tbody className={clsx('divide-y divide-neutral-200 bg-white', className)}>{children}</tbody>
  )
}

export interface TableRowProps {
  children: ReactNode
  className?: string
  onClick?: () => void
  hoverable?: boolean
}

export function TableRow({ children, className, onClick, hoverable = true }: TableRowProps) {
  return (
    <tr
      className={clsx(hoverable && 'hover:bg-neutral-50', onClick && 'cursor-pointer', className)}
      onClick={onClick}
    >
      {children}
    </tr>
  )
}

export interface TableHeaderProps extends ThHTMLAttributes<HTMLTableCellElement> {
  children: ReactNode
}

export function TableHeader({ children, className, ...props }: TableHeaderProps) {
  return (
    <th
      className={clsx(
        'px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider',
        className
      )}
      {...props}
    >
      {children}
    </th>
  )
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  children: ReactNode
}

export function TableCell({ children, className, ...props }: TableCellProps) {
  return (
    <td
      className={clsx('px-4 py-4 text-sm text-neutral-900 whitespace-nowrap', className)}
      {...props}
    >
      {children}
    </td>
  )
}
